/**
 * #1157 E2.2 (Ted #32b) — a customer's packages, with "couldn't load" kept
 * apart from "has none".
 *
 * Ted, 2026-10-06: a profile said "No package balance" while the client held
 * about 34 days. Part of that was the empty table (#994, E2.1), but the page
 * also could not tell the two apart: on ANY failed read it rendered an empty
 * list, so the card said "No package on file" — the one answer that sends the
 * desk to charge a card for a prepaid visit. A failed read is now its own
 * state, with its own words and a Retry.
 *
 * Shared by the owner profile (CustomerDetail) and the pet profile
 * (DogDetail), which shows the OWNER's balance (packages are owner-level:
 * siblings draw from one package).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { adminPaymentsApi } from '../../lib/api.js';
import type { CustomerPackage } from './PackageBalance.js';

export type CustomerPackagesLoad =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'error'; readonly message: string }
  | { readonly kind: 'ready'; readonly packages: readonly CustomerPackage[] };

/** What a failed read says. Never "no package": the balance is unknown, not zero. */
export const PACKAGES_UNAVAILABLE_MESSAGE =
  "Couldn't load the package balance. This does not mean there is no package — check before charging.";

/**
 * Pure: an `ApiResponse` from `listPackages` → the load state. A response
 * with an error, no body, or a body without a `packages` array is a FAILED
 * read (an older server, a 403, a 5xx, a network drop) — never an empty list.
 */
export function packagesLoadFromResponse(res: {
  data?: { packages?: unknown } | null;
  error?: string | null;
}): CustomerPackagesLoad {
  if (res.error || !res.data || !Array.isArray(res.data.packages)) {
    return { kind: 'error', message: PACKAGES_UNAVAILABLE_MESSAGE };
  }
  return { kind: 'ready', packages: res.data.packages as CustomerPackage[] };
}

/**
 * Loads `customerId`'s packages. `enabled: false` (no customer id, or a role
 * without `payments-ops:read:all`) skips the request and stays `idle`, so a
 * floor role is never shown a 403 dressed up as a balance.
 */
export function useCustomerPackages(
  apiUrl: string,
  customerId: string | null | undefined,
  opts: { enabled?: boolean } = {},
): { load: CustomerPackagesLoad; reload: () => Promise<void> } {
  const enabled = (opts.enabled ?? true) && Boolean(customerId);
  const [load, setLoad] = useState<CustomerPackagesLoad>(
    enabled ? { kind: 'loading' } : { kind: 'idle' },
  );
  // A reply for a customer the page has since navigated away from must not
  // land on the new one.
  const latest = useRef(0);

  const reload = useCallback(async () => {
    if (!enabled || !customerId) {
      setLoad({ kind: 'idle' });
      return;
    }
    const seq = ++latest.current;
    setLoad((prev) => (prev.kind === 'ready' ? prev : { kind: 'loading' }));
    let next: CustomerPackagesLoad;
    try {
      next = packagesLoadFromResponse(await adminPaymentsApi.listPackages(apiUrl, customerId));
    } catch {
      next = { kind: 'error', message: PACKAGES_UNAVAILABLE_MESSAGE };
    }
    if (seq === latest.current) setLoad(next);
  }, [apiUrl, customerId, enabled]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { load, reload };
}
